/**
 * Lightweight logging system for SplitFiesta
 * Zero external dependencies, optimized for AWS CloudWatch Logs
 *
 * CloudWatch Optimizations:
 * - Outputs to stdout/stderr (not console.log) for proper stream capture
 * - Uses @timestamp field (CloudWatch Insights standard)
 * - Includes numeric severity levels for easier filtering
 * - No buffering (CloudWatch handles persistence, buffering can lose logs on crash)
 * - Comprehensive error serialization (captures all error properties)
 * - Single-line JSON in production for efficient parsing
 */
class Logger {
    serviceName;
    isDevelopment;
    minLevel;
    levelSeverity = {
        debug: 0,
        info: 1,
        warn: 2,
        error: 3,
        silent: 4, // Nothing logs at this level
    };
    constructor(serviceName) {
        this.serviceName = serviceName;
        // Browser-compatible environment detection
        this.isDevelopment = this.getEnvironment() === 'development';
        this.minLevel = this.getMinLevel();
    }
    getEnvironment() {
        // Browser environment (Vite)
        if (typeof window !== 'undefined' && window.import?.meta?.env) {
            return window.import.meta.env.MODE || 'production';
        }
        // Node.js environment
        if (typeof process !== 'undefined' && process.env) {
            return process.env.NODE_ENV || 'production';
        }
        return 'production';
    }
    getMinLevel() {
        let envLevel;
        // Browser environment (Vite)
        if (typeof window !== 'undefined' && window.import?.meta?.env) {
            envLevel = window.import.meta.env.VITE_LOG_LEVEL;
        }
        // Node.js environment
        else if (typeof process !== 'undefined' && process.env) {
            envLevel = process.env.LOG_LEVEL;
        }
        if (envLevel &&
            ['debug', 'info', 'warn', 'error', 'silent'].includes(envLevel.toLowerCase())) {
            return envLevel.toLowerCase();
        }
        return this.isDevelopment ? 'debug' : 'info';
    }
    shouldLog(level) {
        // Silent mode suppresses all logs
        if (this.minLevel === 'silent') {
            return false;
        }
        const levels = ['debug', 'info', 'warn', 'error'];
        return levels.indexOf(level) >= levels.indexOf(this.minLevel);
    }
    /**
     * JSON.stringify that survives circular references (AWS SDK errors
     * carry an IncomingMessage whose req/res reference each other). A
     * plain stringify would throw here, replacing the error being logged
     * with the logger's own TypeError.
     */
    safeStringify(value, indent) {
        const seen = new WeakSet();
        return JSON.stringify(value, (_key, val) => {
            if (typeof val === 'object' && val !== null) {
                if (seen.has(val)) {
                    return '[Circular]';
                }
                seen.add(val);
            }
            return val;
        }, indent);
    }
    serializeError(error) {
        // Capture all enumerable and non-enumerable properties
        const serialized = {
            message: error.message,
            name: error.name,
            stack: error.stack,
        };
        // Capture any additional properties (like code, statusCode, etc.)
        Object.getOwnPropertyNames(error).forEach(key => {
            if (!serialized[key]) {
                serialized[key] = error[key];
            }
        });
        return serialized;
    }
    formatMessage(entry) {
        // Use JSON format for production (CloudWatch friendly)
        if (!this.isDevelopment) {
            const logEntry = {
                '@timestamp': entry.timestamp, // CloudWatch Insights standard
                service: this.serviceName,
                level: entry.level,
                severity: this.levelSeverity[entry.level], // Numeric for filtering
                message: entry.message,
                ...entry.context,
            };
            // Add error details if present
            if (entry.error) {
                logEntry.error = this.serializeError(entry.error);
            }
            return this.safeStringify(logEntry);
        }
        // Pretty output for development
        let output = `[${this.serviceName}] ${entry.message}`;
        if (entry.context && Object.keys(entry.context).length > 0) {
            output += '\n' + this.safeStringify(entry.context, 2);
        }
        if (entry.error) {
            output += '\n' + entry.error.stack;
        }
        return output;
    }
    log(level, message, context, error) {
        if (!this.shouldLog(level))
            return;
        const entry = {
            level,
            message,
            context: {
                ...context,
                timestamp: Date.now(),
            },
            timestamp: new Date().toISOString(),
            error,
        };
        // Format and output to stdout/stderr (CloudWatch captures these)
        const formatted = this.formatMessage(entry);
        // Use stdout for info/debug, stderr for warn/error (CloudWatch best practice)
        if (level === 'error' || level === 'warn') {
            // Write to stderr for errors and warnings
            if (typeof process !== 'undefined' && process.stderr) {
                process.stderr.write(formatted + '\n');
            }
            else {
                console.error(formatted);
            }
        }
        else {
            // Write to stdout for info and debug
            if (typeof process !== 'undefined' && process.stdout) {
                process.stdout.write(formatted + '\n');
            }
            else {
                console.log(formatted);
            }
        }
    }
    debug(message, context) {
        this.log('debug', message, context);
    }
    info(message, context) {
        this.log('info', message, context);
    }
    warn(message, context) {
        this.log('warn', message, context);
    }
    error(message, errorOrContext, context) {
        if (errorOrContext instanceof Error) {
            this.log('error', message, context, errorOrContext);
        }
        else {
            this.log('error', message, errorOrContext);
        }
    }
    /**
     * Create a child logger with additional context
     */
    child(context) {
        const childLogger = new Logger(this.serviceName);
        const originalLog = childLogger.log.bind(childLogger);
        childLogger.log = (level, message, ctx, error) => {
            originalLog(level, message, { ...context, ...ctx }, error);
        };
        return childLogger;
    }
    /**
     * Performance timing helper
     */
    time(label, context) {
        const start = performance.now();
        return () => {
            const duration = Math.round(performance.now() - start);
            this.info(`${label} completed`, { ...context, duration });
        };
    }
    /**
     * Get logger configuration for debugging
     */
    getConfig() {
        return {
            service: this.serviceName,
            environment: this.getEnvironment(),
            logLevel: this.minLevel,
            isDevelopment: this.isDevelopment,
        };
    }
}
/**
 * Correlation ID utilities
 */
export function generateCorrelationId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
export function extractCorrelationId(headers) {
    return headers['x-correlation-id'] || headers['X-Correlation-Id'];
}
/**
 * Factory function to create loggers
 */
export function createLogger(serviceName) {
    return new Logger(serviceName);
}
